'use client';
import { cn } from '@bells/core/lib/utils';
import NextImage from 'next/image';
import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from '@bells/ui/components/dialog';

export const ZoomableImage = ({
  src,
  zoomSrc = src,
  alt,
  className,
  ...props
}: Omit<ComponentProps<typeof NextImage>, 'src'> & {
  src: string;
  zoomSrc?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [height, setHeight] = useState<number>(0);
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    setHeight(window.innerHeight);
    setWidth(window.innerWidth);

    const handleResize = () => {
      setHeight(window.innerHeight);
      setWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const imageSize = useMemo(() => {
    const aspectRatio = 2 / 3;
    const screenWidth = width * 0.9;
    const screenHeight = height * 0.9;

    let adjustedWidth = screenWidth;
    let adjustedHeight = screenWidth / aspectRatio;

    if (adjustedHeight > screenHeight) {
      adjustedHeight = screenHeight;
      adjustedWidth = screenHeight * aspectRatio;
    }

    return { width: adjustedWidth, height: adjustedHeight };
  }, [width, height]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <NextImage
          unoptimized
          src={src}
          alt={alt || ''}
          className={cn('cursor-zoom-in', className)}
          {...props}
        />
      </DialogTrigger>
      <DialogContent
        className='h-0 max-h-[100vh] w-0 max-w-[100vw] overflow-hidden rounded-md border-0 bg-background p-0 shadow-lg lg:max-h-[90vh] lg:max-w-[90vw] lg:rounded-lg'
        style={{
          width: `${imageSize.width}px`,
          height: `${imageSize.height}px`,
        }}
      >
        <DialogTitle className='hidden'></DialogTitle>
        <NextImage
          unoptimized
          src={zoomSrc}
          loading='lazy'
          fill
          blurDataURL={props.blurDataURL}
          placeholder={props.placeholder}
          className='object-contain'
          alt={alt || ''}
        />
      </DialogContent>
    </Dialog>
  );
};
